import classNames from "classnames";
import { Inline as Property } from "components/Properties";
import { Status } from "components/Shared";
import { UserContext } from "context";
import { icons, translate } from "lib";
import _ from "lodash";
import React, { memo, useContext } from "react";
import { Link } from "react-router-dom";

interface Props {
	showProperty: boolean;
	project: tcpinpoint.Project;
	link?: string;
	badge?: React.ReactNode;
}

/**
 * Render a single Project for a List Group
 * @param {props} Project
 * @param {props} showProperty Should show Property context (Property and Tenancy Name)
 * @param {props} link Optional link override (defaults to the project show page)
 * @param {props} badge Optional badge override (defaults to "Mine" / watching icon)
 */
const Project = ({ showProperty, project, link, badge: badgeOverride }: Props) => {
	const { currentUser } = useContext(UserContext) as { currentUser: tcpinpoint.User };
	const startedByUser = currentUser.id === project.created_by_id;
	const badge = badgeOverride ?? (!!startedByUser ? translate("mine") : <i className={`far fa-${icons.watching}`} />);
	const leaseStatus = _.replace(translate(_.get(project, "lease_status")), "unknown", "");
	// Flattened onto the project by ProjectSerializer, so the list needs no extra
	// include. Rendered raw — Template::AMENDMENT_TYPES are already human-readable
	// ("Original Lease", "Renewal", "Termination"), which is how the project show
	// page renders it too.
	const amendmentType = _.get(project, "lease_amendment_type");
	const parent = !!showProperty && <Property property={project.property_summary} />;
	const heading = project.name || _.get(project, "tenant_name", false) || "Unnamed";
	// Every unit the project is on ("Shop 1, Shop 2"), not the legacy singular
	// `tenancy_name`, which only ever named one (often "Unallocated Space").
	// Falls back for an API that predates `tenancy_names`.
	const tenancyNames = _.get(project, "tenancy_names") || _.get(project, "tenancy_name");
	const subheading = _.compact([
		!!showProperty ? tenancyNames !== project.name && tenancyNames : null,
		_.get(project, "building_name") ? ` at ${_.get(project, "building_name")}` : null,
	]).join("");
	const dateKey = project.closed ? "archived_after" : project.complete ? "completed_in" : "open_for";
	const daysCount = project.closed
		? project.days_to_archived
		: project.complete
		? project.days_to_completed
		: project.days_active;
	const dateValue = `${daysCount} ${daysCount > 1 ? "days" : "day"}`;
	const projectClassNames = classNames([
		"project",
		{
			"project-open": !!project.open,
			"project-closed": !!project.closed,
			"project-complete": !!project.complete,
			"project-incomplete": !!project.incomplete,
			"project-ahead": !!project.ahead,
			"project-behind": !!project.behind,
			"project-deleted": !!project.deleted,
		},
		"list-group-item",
	]);

	return (
		<>
			<Link
				to={link || `/projects/${_.get(project, "id")}`}
				className={projectClassNames}
				data-heading={heading}
				data-subheading={subheading}
			>
				<div className="stamp">
					<Status closed={!!project.closed} incomplete={!project.complete} is_defects={project.is_defects} />
				</div>
				<span className="list-group-item-content">
					{(!!heading || !!subheading) && (
						<div className="list-group-item-heading">
							{!!heading && <h3>{heading}</h3>}
							{!!subheading && <h6>{subheading}</h6>}
						</div>
					)}
					{!!parent && <div className="list-group-item-parent">{parent}</div>}
				</span>
				{/* Lease Status */}
				<div className="list-group-item-statistics">
					<h6>{leaseStatus}</h6>
				</div>
				{/* Amendment Type. The column is always rendered so its fixed width keeps
				    the rows aligned, but it only paints when the lease has a value. */}
				<div className="list-group-item-statistics">
					{!!amendmentType && (
						<div className="statistics-item">
							<h6>{translate("amendment_type")}</h6>
							<p>{amendmentType}</p>
						</div>
					)}
				</div>
				{/* Open / Completed / Closed Days */}
				<div className="list-group-item-datapoints">
					<div data-key={dateKey} className="list-group-item-datapoint">
						<h6>{translate(dateKey)}</h6>
						{dateValue}
					</div>
				</div>
				<div className="badge">{badge}</div>
				<div className="disclosure">
					<i className="fas fa-angle-right" />
				</div>
			</Link>
		</>
	);
};

export default memo(Project);
